import React, { useState } from 'react';

export default function GeneralInfoModal({ serviceUser, onClose }) {
    const [activeTab, setActiveTab] = useState('personal');

    const addressData = serviceUser.address || {};
    const gpData = serviceUser.gpInfo || {};
    const pharmacyData = serviceUser.pharmacyInfo || {};
    const socialWorkerData = serviceUser.socialWorker || {};
    const fundingData = serviceUser.fundingBody || {};
    const nextOfKinData = serviceUser.nextOfKin || {};

    const Field = ({ label, value }) => (
        <div style={{ marginBottom: '15px', minWidth: '200px', flex: '1' }}>
            <label style={{ display: 'block', fontSize: '11px', textTransform: 'uppercase', color: '#888', marginBottom: '4px', fontWeight: 'bold', letterSpacing: '0.5px' }}>{label}</label>
            <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', wordBreak: 'break-word' }}>{value || '-'}</div>
        </div>
    );

    const Section = ({ title, children }) => (
        <div style={{ 
            backgroundColor: '#f9fafb', 
            borderRadius: '8px', 
            padding: '20px', 
            marginBottom: '20px',
            border: '1px solid #e5e7eb'
        }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', fontSize: '16px' }}>{title}</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                {children}
            </div>
        </div>
    );

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>General Information</h2>
                    <button onClick={onClose} style={styles.closeBtn}>×</button>
                </div>
                
                <div style={styles.tabsContainer}>
                    {['personal', 'contact', 'next of kin', 'care & medical', 'professional contacts', 'billing'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            style={{
                                ...styles.tabBtn,
                                backgroundColor: activeTab === tab ? '#eff6ff' : 'transparent',
                                color: activeTab === tab ? '#2563eb' : '#4b5563',
                                borderBottom: activeTab === tab ? '2px solid #2563eb' : '2px solid transparent'
                            }}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                <div style={styles.scrollArea}>
                    {activeTab === 'personal' && (
                        <Section title="Personal Information">
                            <Field label="Full Name" value={serviceUser.fullName} />
                            <Field label="Known As" value={serviceUser.knownAs} />
                            <Field label="Date of Birth" value={serviceUser.dob} />
                            <Field label="Gender" value={serviceUser.gender} />
                            <Field label="Nationality" value={serviceUser.nationality} />
                            <Field label="Ethnicity" value={serviceUser.ethnicity} />
                            <Field label="Religion" value={serviceUser.religion} />
                            <Field label="Marital Status" value={serviceUser.maritalStatus} />
                            <Field label="Sexual Orientation" value={serviceUser.sexualOrientation} />
                            <Field label="Preferred Language" value={serviceUser.preferredLanguage} />
                            <Field label="NI Number" value={serviceUser.niNumber} />
                            <Field label="NHS Number" value={serviceUser.nhsNumber} />
                        </Section>
                    )}

                    {activeTab === 'contact' && (
                        <>
                        <Section title="Address Details">
                            <Field label="Flat / Building" value={addressData.secondLineOfAddress} />
                            <Field label="Street / First Line" value={addressData.firstLineOfAddress} />
                            <Field label="Town / City" value={addressData.town} />
                            <Field label="County" value={addressData.county} />
                            <Field label="Postcode" value={addressData.postalCode} />
                            <Field label="Country" value={addressData.country} />
                            <Field label="Access Keycode" value={serviceUser.keycode} />
                        </Section>
                        <Section title="Contact Info">
                            <Field label="Email" value={serviceUser.email} />
                            <Field label="Primary Phone" value={`${serviceUser.phoneCode || ''} ${serviceUser.phoneNumber || ''}`} />
                            <Field label="Secondary Phone" value={serviceUser.secondaryTel} />
                        </Section>
                        </>
                    )}

                    {activeTab === 'next of kin' && (
                        <Section title="Next of Kin / Emergency Contact">
                            <Field label="Name" value={nextOfKinData.name} />
                            <Field label="Relation" value={nextOfKinData.relation} />
                            <Field label="Phone" value={nextOfKinData.phone} />
                            <Field label="Email" value={nextOfKinData.email} />
                            <Field label="Address" value={nextOfKinData.address} />
                        </Section>
                    )}

                    {activeTab === 'care & medical' && (
                        <>
                        <Section title="Medical Overview">
                            <Field label="Mental State" value={serviceUser.mentalState} />
                            <Field label="DNACPR Status" value={serviceUser.dnacpr} />
                            <Field label="Care Requirement" value={serviceUser.careRequirementLevelTag} />
                        </Section>
                        <Section title="GP Information">
                            <Field label="GP Name" value={gpData.gpName} />
                        </Section>
                        <Section title="Pharmacy Information">
                            <Field label="Pharmacy Name" value={pharmacyData.pharmacyName} />
                        </Section>
                        </>
                    )}

                    {activeTab === 'professional contacts' && (
                        <>
                        <Section title="Social Worker">
                            <Field label="Name" value={socialWorkerData.name} />
                            <Field label="Organisation" value={socialWorkerData.organisation} />
                            <Field label="Email" value={socialWorkerData.email} />
                            <Field label="Phone" value={socialWorkerData.phone} />
                            <Field label="Emergency Contact" value={socialWorkerData.emergencyContact} />
                        </Section>
                        <Section title="Power of Attorney (POA)">
                            {serviceUser.poa && serviceUser.poa.length > 0 ? (
                                serviceUser.poa.map((p, index) => (
                                    <div key={index} style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px dashed #e5e7eb', width: '100%' }}>
                                        <strong style={{ color: '#111827' }}>{p.name}</strong> - <span style={{ color: '#6b7280' }}>{p.type}</span>
                                    </div>
                                ))
                            ) : (
                                <div style={{ color: '#6b7280' }}>No POA recorded.</div>
                            )}
                        </Section>
                        </>
                    )}

                    {activeTab === 'billing' && (
                        <Section title="Funding & Charges">
                            <Field label="Funding Type" value={fundingData.fundingType} />
                            <Field label="Funding Agreed?" value={serviceUser.fundingAgreedTag} />
                            <div style={{ width: '100%', height: '1px', background: '#e5e7eb', margin: '15px 0' }}></div>
                            <Field label="Primary Hourly Rate" value={`£${serviceUser.charge?.primaryHourlyCharge || 0}`} />
                            <Field label="Bank Holiday Rate" value={`£${serviceUser.charge?.bankHolidayHourlyCharge || 0}`} />
                            <Field label="Mileage Charge" value={`£${serviceUser.charge?.mileageCharge || 0} / mile`} />
                            <Field label="Invoice Frequency" value={serviceUser.charge?.primaryFrequency} />
                        </Section>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
    modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
    tabsContainer: { display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '0 24px', overflowX: 'auto' },
    tabBtn: { padding: '12px 16px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: '600', textTransform: 'capitalize', fontSize: '14px', whiteSpace: 'nowrap' },
    scrollArea: { padding: '24px', overflowY: 'auto', flex: 1 }
};
